import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, CheckCircle2, Star, Users, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";

// Use environment variable with fallback to current origin
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function GetMatchedPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    property_address: "Not provided",
    city: "",
    postal_code: "",
    property_type: "",
    num_units: 1,
    management_status: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);

  // Detect user's location on component mount
  React.useEffect(() => {
    const detectLocation = async () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            
            try {
              // Use reverse geocoding to get city
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
              );
              const data = await response.json();
              
              if (data.address) {
                const city = data.address.city || data.address.town || data.address.municipality || "";
                const postalCode = data.address.postcode || "";
                
                setFormData(prev => ({
                  ...prev,
                  city: city,
                  postal_code: postalCode
                }));
                setLocationDetected(true);
              }
            } catch (error) {
              console.log("Could not detect location:", error);
            }
          },
          (error) => {
            console.log("Geolocation not available:", error);
          }
        );
      }
    };
    
    detectLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Track form start event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'form_start',
        'form_name': 'property_management_lead',
        'form_location': 'get_matched_page'
      });
    }

    try {
      const response = await axios.post(`${API}/leads`, {
        ...formData,
        num_units: parseInt(formData.num_units),
        disclaimer_accepted: true
      });

      // Track successful conversion
      if (window.dataLayer && response.data.id) {
        window.dataLayer.push({
          'event': 'form_submission_success',
          'form_name': 'property_management_lead',
          'conversion_type': 'property_management',
          'lead_id': response.data.id,
          'city': formData.city,
          'property_type': formData.property_type
        });
      }

      // Redirect to results page
      navigate(`/results/${response.data.id}`);
    } catch (error) {
      console.error("Error submitting lead:", error);
      
      // Track form error
      if (window.dataLayer) {
        window.dataLayer.push({
          'event': 'form_submission_error',
          'form_name': 'property_management_lead',
          'error_message': error.response?.data?.detail || 'Submission failed'
        });
      }
      
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header with Full Navigation */}
      <Header variant="full" />

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          {/* Headline Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight" style={{ color: '#1E1E1E' }}>
              Get Matched with Top Property Management Companies in Your Area
            </h1>
            <p className="text-lg sm:text-xl mb-6" style={{ color: '#3A3A3A' }}>
              Receive up to 10 free quotes from verified property managers. Compare services and choose the best fit for your property.
            </p>
            
            {/* Trust Signals */}
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" style={{ color: '#3b82f6' }} />
                <span className="font-medium" style={{ color: '#1E1E1E' }}>100% Free</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5" style={{ color: '#F4A261' }} />
                <span className="font-medium" style={{ color: '#1E1E1E' }}>Verified Companies</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" style={{ color: '#F4A261' }} />
                <span className="font-medium" style={{ color: '#1E1E1E' }}>10,000+ Units Served</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" style={{ color: '#3b82f6' }} />
                <span className="font-medium" style={{ color: '#1E1E1E' }}>Canada Wide</span>
              </div>
            </div>
          </div>

          {/* Form Card */}
          <Card className="bg-white border-slate-200 shadow-2xl">
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="border-slate-300 h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700">Email *</Label>
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      className="border-slate-300 h-12"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700">Phone Number (Cell Phone Required) *</Label>
                    <Input
                      id="phone"
                      data-testid="phone-input"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="border-slate-300 h-12"
                    />
                    <p className="text-xs text-blue-600 font-medium">📱 Must be a cell phone that can receive text messages with your matches</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-slate-700">City *</Label>
                    <Input
                      id="city"
                      data-testid="city-input"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Vancouver, Toronto, Montreal"
                      className="border-slate-300 h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code" className="text-slate-700">Postal Code *</Label>
                  <Input
                    id="postal_code"
                    data-testid="postal-code-input"
                    required
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="e.g., V6B 3K9, M5V 2T6, H2Y 1C6"
                    className="border-slate-300 h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="property_type" className="text-slate-700">Property Type *</Label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                    required
                  >
                    <SelectTrigger className="border-slate-300 h-12" data-testid="property-type-select">
                      <SelectValue placeholder="Select property type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">Residential Property</SelectItem>
                      <SelectItem value="condo">Condo / Strata</SelectItem>
                      <SelectItem value="commercial">Commercial Property</SelectItem>
                      <SelectItem value="multi-family">Multi-Family Property</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="management_status" className="text-slate-700">Current Management Status *</Label>
                  <Select
                    value={formData.management_status}
                    onValueChange={(value) => setFormData({ ...formData, management_status: value })}
                    required
                  >
                    <SelectTrigger className="border-slate-300 h-12" data-testid="status-select">
                      <SelectValue placeholder="Select current status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self-managed">Self Managed</SelectItem>
                      <SelectItem value="looking-to-switch">Looking to Switch</SelectItem>
                      <SelectItem value="new-property">New Property</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit Button - Large and Prominent */}
                <Button
                  type="submit"
                  className="w-full text-white font-bold py-6 text-lg transition-all duration-300"
                  style={{ 
                    backgroundColor: '#3b82f6',
                    borderRadius: '12px',
                    boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.currentTarget.style.background = 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(59, 130, 246, 0.4)';
                      e.currentTarget.style.color = '#000000';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#3b82f6';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.3)';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  disabled={submitting}
                  data-testid="submit-button"
                >
                  {submitting ? "Submitting..." : "Get My Free Quotes Now"}
                </Button>

                {/* Trust Message */}
                <p className="text-center text-sm" style={{ color: '#9C9C9C' }}>
                  By submitting, you agree to receive quotes from property management companies. No obligation to hire.
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Additional Trust Elements */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-6 py-3">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span className="text-green-800 font-medium">Get instant access to top-rated property managers</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-white py-12 mt-12" style={{ backgroundColor: '#1E1E1E' }}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6" style={{ color: '#3b82f6' }} />
              <span className="text-xl font-bold">Top Prop</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <button
                onClick={() => navigate("/contact")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Contact Us
              </button>
              <span style={{ color: '#3A3A3A' }}>|</span>
              <button
                onClick={() => navigate("/company-signup")}
                className="transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Join as Property Manager
              </button>
            </div>
            <p className="text-sm" style={{ color: '#9C9C9C' }}>© 2025 Top Prop. All rights reserved.</p>
            
            {/* Admin Login Link */}
            <div className="mt-4">
              <button
                onClick={() => navigate("/admin-login")}
                className="text-xs underline transition-colors duration-300"
                style={{ color: '#9C9C9C' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#F4A261'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9C9C9C'}
              >
                Admin Login
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
